import { useState } from "preact/hooks";
import type { UiMode } from "@shared/schemas";
import { authClient } from "../lib/auth-client";
import { api } from "../lib/api-client";
import { useModalDialog } from "../lib/use-modal-dialog";
import { useNavigationGuard } from "../lib/navigation-guard";
import { useToast } from "../lib/use-toast";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  SegmentedControl,
} from "./ui";

// Map the current route to its equivalent in the target mode so a switch keeps
// the user on the same content/list instead of bouncing to Home. Only the
// project content/list routes have a 1:1 mapping; anything else falls back to
// "/" and lets home.tsx route by uiMode.
export function destForMode(
  path: string,
  next: UiMode,
): string {
  const clean = path.split(/[?#]/)[0]!;
  const m = clean.match(
    /^\/projects\/([^/]+)(?:\/(?:contents|pages)(?:\/([^/]+))?)?\/?$/,
  );
  if (!m) return "/";
  const [, projectId, slug] = m;
  if (next === "advanced") {
    return slug
      ? `/projects/${projectId}/contents/${slug}`
      : `/projects/${projectId}`;
  }
  return slug
    ? `/projects/${projectId}/pages/${slug}`
    : `/projects/${projectId}/pages`;
}

// Two-state segmented control for the user's UI preference. Persists to the
// server, then triggers a session refetch so the rest of the app sees the new
// uiMode immediately, then hard-reloads to the equivalent route in the new mode
// (destForMode) — falling back to Home when the current path has no mapping.
export function HeaderModeToggle({
  uiMode,
  currentPath,
}: {
  uiMode: UiMode;
  currentPath: string;
}) {
  const [busy, setBusy] = useState<UiMode | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { confirmLeave } = useNavigationGuard();
  const toast = useToast();

  async function commitSwitch(next: UiMode) {
    setBusy(next);
    try {
      await api.updateUiMode(next);
      try {
        await authClient.getSession({ query: { disableCookieCache: true } });
      } catch {
        /* ignore — hard redirect below handles it */
      }
      window.location.href = destForMode(currentPath, next);
    } catch (e) {
      setBusy(null);
      setConfirmOpen(false);
      toast.error(`モードを切り替えられませんでした: ${(e as Error).message}`);
    }
  }

  async function onSelect(next: UiMode) {
    if (next === uiMode || busy) return;
    // The switch ends in a full-page load. Ask about unsaved edits up front,
    // before the server-side mode changes, so declining leaves nothing half
    // switched (and beforeunload doesn't prompt a second time).
    if (!(await confirmLeave())) return;
    // Advanced exposes destructive surfaces (API keys, project deletion, raw
    // frontmatter). Gate that direction on confirmation; advanced → simple is
    // a benign narrowing of the UI so it switches immediately.
    if (uiMode === "simple" && next === "advanced") {
      setConfirmOpen(true);
      return;
    }
    commitSwitch(next);
  }

  return (
    <>
      <SegmentedControl
        ariaLabel="UI モード"
        size="sm"
        value={uiMode}
        onChange={(next) => void onSelect(next)}
        options={[
          { value: "simple", label: busy === "simple" ? "…" : "シンプル", disabled: busy !== null },
          { value: "advanced", label: busy === "advanced" ? "…" : "Advanced", disabled: busy !== null },
        ]}
      />
      {confirmOpen && (
        <SwitchToAdvancedDialog
          busy={busy === "advanced"}
          onConfirm={() => commitSwitch("advanced")}
          onCancel={() => setConfirmOpen(false)}
        />
      )}
    </>
  );
}

function SwitchToAdvancedDialog({
  busy,
  onConfirm,
  onCancel,
}: {
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  // showModal() handles Esc + focus trap natively; the hook mirrors its close
  // event into onCancel so the node is torn down (otherwise re-opening wouldn't
  // call showModal again because el.open is already false).
  const ref = useModalDialog({ onClose: onCancel });

  function onBackdropClick(e: MouseEvent) {
    // The dialog element itself fills the viewport in modal mode; clicks on
    // its inner Card bubble with target === Card descendants, so a target of
    // the dialog itself means the user clicked the backdrop region.
    if (e.target === ref.current && !busy) ref.current?.close();
  }

  return (
    <dialog
      ref={ref}
      onClick={onBackdropClick}
      class="m-auto rounded-lg border-0 bg-transparent p-0 backdrop:bg-fg/40"
    >
      <Card elevated class="w-[min(92vw,28rem)]">
        <CardHeader>
          <h2 class="text-base font-semibold text-fg">
            Advanced モードに切り替えますか？
          </h2>
        </CardHeader>
        <CardBody class="space-y-3 text-sm text-fg-muted">
          <p>シンプルモードで隠している以下の機能が表に出ます:</p>
          <ul class="space-y-1 pl-5 [&>li]:list-disc">
            <li>生の Markdown エディタ (frontmatter / 構文を直接編集)</li>
            <li>プロジェクト設定 (デプロイフック / 新規記事テンプレート)</li>
            <li>API キーの発行・失効</li>
            <li>プロジェクトの削除 (取消不可)</li>
          </ul>
          <p class="text-fg-subtle">
            誤操作するとコンテンツや設定を失う可能性があります。ヘッダーのトグルからいつでもシンプルに戻せます。
          </p>
        </CardBody>
        <CardFooter class="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onCancel} disabled={busy}>
            キャンセル
          </Button>
          <Button variant="primary" size="sm" onClick={onConfirm} loading={busy}>
            Advanced に切替
          </Button>
        </CardFooter>
      </Card>
    </dialog>
  );
}
